import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getModoIdentificar, onModoIdentificarChange, setModoIdentificar } from '../lib/modo-identificar';
import { CATALOGO_COMPONENTES } from '../modules/configuracoes/catalogo-componentes';

interface Selo {
  id: string;
  numero: string;
  nome: string;
  rect: DOMRect;
}

// Mapa numero -> nome (carregado uma vez)
const MAPA_NOME: Record<string, string> = {};
for (const p of CATALOGO_COMPONENTES) {
  MAPA_NOME[p.numero] = p.nome;
}

// Componente montado UMA vez no Layout.
// So renderiza algo quando o usuario e admin E o modo esta ligado.
export function IdentificadorComponentes() {
  const { usuario } = useAuth();
  const location = useLocation();
  const [ativo, setAtivo] = useState(() => getModoIdentificar());
  const [selos, setSelos] = useState<Selo[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  // Sincroniza com localStorage via CustomEvent
  useEffect(() => {
    return onModoIdentificarChange((v) => setAtivo(v));
  }, []);

  const escanear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const elementos = document.querySelectorAll<HTMLElement>('[data-componente]');
      const novos: Selo[] = [];
      elementos.forEach((el) => {
        const num = el.getAttribute('data-componente');
        if (!num) return;
        // Verifica se esta visivel (nao oculto)
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return;
        // Verifica se esta na viewport (ao menos parcialmente)
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        if (rect.right < 0 || rect.left > window.innerWidth) return;
        const nome = MAPA_NOME[num] ?? `Componente ${num}`;
        novos.push({
          id: `${num}-${rect.top.toFixed(0)}-${rect.left.toFixed(0)}`,
          numero: num,
          nome,
          rect,
        });
      });
      setSelos(novos);
    }, 200);
  }, []);

  // Escaneia na montagem, na rota e quando o modo liga
  useEffect(() => {
    if (!ativo || usuario?.perfil !== 'admin') {
      setSelos([]);
      return;
    }
    escanear();
  }, [ativo, location.pathname, usuario, escanear]);

  // MutationObserver para detectar abertura de modais/dropdowns
  useEffect(() => {
    if (!ativo || usuario?.perfil !== 'admin') return;

    observerRef.current = new MutationObserver(() => escanear());
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [ativo, usuario, escanear]);

  // Re-escaneia no scroll e resize (com debounce leve)
  useEffect(() => {
    if (!ativo || usuario?.perfil !== 'admin') return;
    window.addEventListener('scroll', escanear, { passive: true, capture: true });
    window.addEventListener('resize', escanear, { passive: true });
    return () => {
      window.removeEventListener('scroll', escanear, true);
      window.removeEventListener('resize', escanear);
    };
  }, [ativo, usuario, escanear]);

  if (!ativo || usuario?.perfil !== 'admin') return null;

  return (
    <>
      {/* Contornos e selos dos componentes */}
      {selos.map((s) => (
        <Marca key={s.id} selo={s} />
      ))}

      {/* Etiqueta flutuante "Modo identificar ligado — desligar" */}
      <button
        type="button"
        onClick={() => setModoIdentificar(false)}
        style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          zIndex: 9999,
          background: '#fde68a',
          color: '#000',
          border: 'none',
          borderRadius: '0.375rem',
          padding: '0.25rem 0.75rem',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          pointerEvents: 'auto',
        }}
      >
        Modo identificar ligado — desligar
      </button>
    </>
  );
}

// Sub-componente para cada contorno + selo (evita re-renderizacao da lista toda)
function Marca({ selo }: { selo: Selo }) {
  const { rect, numero, nome } = selo;

  return (
    <>
      {/* Contorno via box-shadow fixed simulado com div */}
      <div
        style={{
          position: 'fixed',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          outline: '2px solid var(--primary, #2563eb)',
          outlineOffset: '1px',
          pointerEvents: 'none',
          zIndex: 9997,
          borderRadius: 2,
        }}
      />
      {/* Selo como etiqueta de aba ACIMA da borda — fora do componente, pra nao cobrir
          o conteudo (campo, botao) nem atrapalhar a digitacao. Se nao ha espaco acima
          (elemento colado no topo da viewport), desce pra logo abaixo da borda superior. */}
      <div
        style={{
          position: 'fixed',
          // 17px ~ altura do selo: encosta a base do selo na borda superior do elemento.
          top: rect.top - 17 >= 0 ? rect.top - 17 : rect.top + 1,
          left: rect.left,
          zIndex: 9998,
          background: '#fde68a',
          color: '#000',
          fontSize: 10,
          fontWeight: 700,
          padding: '1px 5px',
          borderRadius: '3px 3px 0 0',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          lineHeight: 1.5,
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          fontFamily: 'monospace',
          letterSpacing: 0,
        }}
      >
        {numero} · {nome}
      </div>
    </>
  );
}
